/**
 * NOTE: This code belongs to Kangaroo Rewards. Unauthorized use is prohibited
 */

@file:Suppress("unused", "MemberVisibilityCanBePrivate", "EXPERIMENTAL_API_USAGE")
@file:OptIn(ExperimentalJsExport::class)
package features.userBusinessDrawsParticipations

import kangaroorewards.appsdk.core.api.Api
import kangaroorewards.appsdk.core.domain.Result
import kangaroorewards.appsdk.core.domain.Result.Idle
import kangaroorewards.appsdk.core.domain.Result.Loading
import kangaroorewards.appsdk.core.domain.SerializedResult
import kangaroorewards.appsdk.core.domain.toJsonResult
import kangaroorewards.appsdk.core.utils.CFlow
import kangaroorewards.appsdk.core.utils.asCFlow
import kotlinx.coroutines.ExperimentalCoroutinesApi
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.mapLatest
import kotlinx.serialization.InternalSerializationApi
import kotlin.js.ExperimentalJsExport
import features.userBusinessDrawsParticipations.internal.UserBusinessDrawsParticipationsUseCase
import features.userBusinessDrawsParticipations.internal.UserBusinessDrawsParticipationsUseCase.Params
import features.userBusinessDrawsParticipations.models.ParticipationsModel


typealias UserBusinessDrawsParticipationsState = CFlow<UserBusinessDrawsParticipationsUseCaseResult?>

typealias UserBusinessDrawsParticipationsUseCaseResult = Result<ParticipationsModel>

fun UserBusinessDrawsParticipationsUseCaseResult.serializeUserBusinessDrawsParticipationsApiResult(): SerializedResult<String> {
    return this.toJsonResult<ParticipationsModel>()
}

/**
 * Serializes [UserBusinessDrawsParticipationsState]'s data and returns a new
 * [CFlow] containing data serialized as a JSON [String].
 */
@OptIn(ExperimentalCoroutinesApi::class)
fun UserBusinessDrawsParticipationsState.serializeUserBusinessDrawsParticipationsState(): CFlow<SerializedResult<String>?> {
    return this.mapLatest {
        it?.toJsonResult<ParticipationsModel>()
    }.asCFlow()
}

/**
 * Get a list of draws participations for this user for a specific business. [Api] class
 */
class UserBusinessDrawsParticipationsApi : Api() {

    val userBusinessDrawsParticipationsState: UserBusinessDrawsParticipationsState
        get() = _userBusinessDrawsParticipationsState.asCFlow()

    companion object {
        private val _userBusinessDrawsParticipationsState: MutableStateFlow<UserBusinessDrawsParticipationsUseCaseResult> = MutableStateFlow(Idle())
    }

    private val userBusinessDrawsParticipationsUseCase = UserBusinessDrawsParticipationsUseCase()

    /**
     * Get a list of draws participations for this user for a specific business.
     */
    suspend fun getUserBusinessDrawsParticipations(
        overrideHeaders: Map<String, String>? = null,
        businessId: String
    ): Result<ParticipationsModel> {
        /* set state to Loading as soon as function is called */
        _userBusinessDrawsParticipationsState.value = Loading()
        val result = this.userBusinessDrawsParticipationsUseCase(
            Params(
                overrideHeaders = overrideHeaders,
                businessId = businessId
            )
        ) { result ->
            _userBusinessDrawsParticipationsState.value = result
            return@userBusinessDrawsParticipationsUseCase result
        }
        return result
    }

}
